
import type React from "react"

import { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { SendIcon, Loader2 } from "lucide-react"
import { format } from "date-fns"
import {
  useMyConversation,
  useChatMessages,
  useSendMessage,
  useMarkRead,
} from "@/api/chat"

export function UserChat() {
  const { t } = useTranslation()
  const [message, setMessage] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { data: conversation, isLoading: convLoading } = useMyConversation()
  const conversationId = conversation?._id
  const { data: msgData, isLoading: msgLoading } = useChatMessages(conversationId)
  const sendMutation = useSendMessage()
  const markReadMutation = useMarkRead()

  const messages = msgData?.messages ?? []

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  useEffect(() => {
    if (conversationId) {
      markReadMutation.mutate(conversationId)
    }
  }, [conversationId])

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim() || !conversationId || sendMutation.isPending) return

    sendMutation.mutate(
      { conversationId, content: message.trim() },
      { onSuccess: () => setMessage("") },
    )
  }

  const isLoading = convLoading || msgLoading

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">{t("userView.supportChat.title")}</h1>

      <Card className="h-[calc(100vh-220px)] flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle>{t("userView.supportChat.supportChat")}</CardTitle>
          <CardDescription>{t("userView.supportChat.description")}</CardDescription>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="h-full pr-4">
              <div className="space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    Send a message to start chatting with the gym.
                  </div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg._id}
                    className={cn(
                      "flex items-start gap-3",
                      msg.senderType === "user" ? "flex-row-reverse" : "flex-row",
                    )}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>{msg.senderType === "user" ? "U" : "O"}</AvatarFallback>
                    </Avatar>
                    <div
                      className={cn(
                        "rounded-lg px-4 py-2 max-w-[80%]",
                        msg.senderType === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted",
                      )}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {format(new Date(msg.createdAt), "p")}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          )}
        </CardContent>

        <CardFooter>
          <form onSubmit={handleSendMessage} className="flex items-center gap-2 w-full">
            <Input
              placeholder={t("userView.supportChat.typeMessage")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="flex-1"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!message.trim() || sendMutation.isPending}
            >
              {sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SendIcon className="h-4 w-4" />
              )}
              <span className="sr-only">{t("userView.supportChat.send")}</span>
            </Button>
          </form>
        </CardFooter>
      </Card>
    </div>
  )
}
